import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, ChevronLeft, Trophy, ArrowRight } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useChampionship } from '@/contexts/ChampionshipContext';
import volleyballApi from '@/services/api';

export const SearchResultsPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { selectChampionship } = useChampionship();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searched, setSearched] = useState(false);

  React.useEffect(() => {
    if (query) {
      performSearch(query);
    } else {
      setLoading(false);
    }
  }, [query]);

  const performSearch = async (searchTerm) => {
    setLoading(true);
    setSearched(true);
    try {
      const data = await volleyballApi.searchTeam(searchTerm);
      setResults(data.results || []);
    } catch (error) {
      console.error('Error searching:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChampionshipClick = async (result) => {
    try {
      const championship = await volleyballApi.getChampionship(result.championship.id);
      selectChampionship(championship);
      navigate(`/championship/${result.championship.id}`);
    } catch (error) {
      console.error('Error loading championship:', error);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-6 w-6 text-gray-600 dark:text-gray-400" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                Risultati ricerca
              </h1>
              {query && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  "{query}"
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Ricerca in corso...</p>
          </div>
        ) : !searched ? (
          <div className="text-center py-12">
            <Search className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              Inserisci un termine di ricerca
            </p>
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-12">
            <Search className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              Nessuna squadra trovata per "{query}"
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
              Prova con un altro nome
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {results.length} {results.length === 1 ? 'risultato trovato' : 'risultati trovati'}
              </p>
            </div>

            {results.map((result, index) => (
              <div
                key={index}
                onClick={() => handleChampionshipClick(result)}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 
                         p-4 hover:shadow-md transition-all duration-200 cursor-pointer
                         hover:border-blue-500 dark:hover:border-blue-400"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-lg mb-2">
                      {result.teamName}
                    </h3>
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                      <Trophy className="h-4 w-4" />
                      <span>{result.championship.name}</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {result.championship.fullName}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
